import React, { useCallback, useEffect, useRef, useState } from 'react';

import { Alert, Col, Divider, Row, Switch, Tag, Typography } from 'antd';
import { Icon } from '@eduzz/ui-antd-theme';

import CharacterCounter from 'components/CharacterCounter';
import { constants } from 'helpers/constants';
import { EChargeType } from 'interfaces/IEditProduct';
import type IProduct from 'interfaces/IProduct';
import { EPartnerCode } from 'interfaces/IProduct';
import { isLegalPersonAccount } from 'v2/helpers/isLegalPersonAccount';
import eduzzAccountsService from 'v2/services/eduzzAccount';
import { useUserStore } from 'v2/store/User';
import { run } from 'validations/ruleRunner';

import { FormContent } from '../FormContent';
import AlertsDefaultOptions from './AlertsDefaultOptions';
import Validations from './validations';

interface IProps {
  loading: boolean;
  fade?: any;
  product?: IProduct;
  modifieds?: any[];
  editing?: any;
  handleChange?: any;
  copyFromOriginal?: any;
  onChangeRequiredFields?: any;
}

type PaymentMethod = {
  label: string;
  id: string;
  checked: boolean;
  helperText: string | React.ReactNode;
  show: boolean;
  error?: string;
  disabled?: boolean;
  chip?: string;
  stateAlert?: AlertsDefaultOptionsStateName;
};

export type AlertsDefaultOptionsStateName =
  | 'showAlertPix'
  | 'showAlertAutomaticPix'
  | 'showAlertCard'
  | 'showAlertBoleto'
  | 'showAlertMultipleCards'
  | 'showAlertEduzzBalance'
  | 'showAlertCombinedPayment';

const paymentShouldConfirmToDisable = [
  'allows_boleto',
  'allows_card',
  'allows_multiple_cards',
  'allows_pix',
  'allows_automatic_pix',
  'allows_eduzz_balance',
  'allows_combine_payment'
];

const FIELD_TO_ALERT_STATE: Record<string, AlertsDefaultOptionsStateName> = {
  allows_boleto: 'showAlertBoleto',
  allows_pix: 'showAlertPix',
  allows_automatic_pix: 'showAlertAutomaticPix',
  allows_card: 'showAlertCard',
  allows_multiple_cards: 'showAlertMultipleCards',
  allows_eduzz_balance: 'showAlertEduzzBalance',
  allows_combine_payment: 'showAlertCombinedPayment'
};

function PaymentMethods(props: IProps) {
  const { loading, handleChange, product, onChangeRequiredFields } = props;
  const originalUserType = useUserStore(state => state.originalUser?.type);
  const isLegalPerson = isLegalPersonAccount(originalUserType);

  const [_showErrors, _setShowErrors] = useState(false);
  const [validationErrors, setValidationErrors] = useState<any>(() => run(product, Validations));
  const [showAlertPix, setShowAlertPix] = useState(false);
  const [showAlertAutomaticPix, setShowAlertAutomaticPix] = useState(false);
  const [showAlertCard, setShowAlertCard] = useState(false);
  const [showAlertBoleto, setShowAlertBoleto] = useState(false);
  const [showAlertMultipleCards, setShowAlertMultipleCards] = useState(false);
  const [showAlertEduzzBalance, setShowAlertEduzzBalance] = useState(false);
  const [showAlertCombinedPayment, setShowAlertCombinedPayment] = useState(false);

  const prevProductRef = useRef<IProduct | undefined>(undefined);

  const errorFor = useCallback((field: any) => validationErrors[field] || '', [validationErrors]);
  const isValid = useCallback((field: any) => (validationErrors[field] ? true : false), [validationErrors]);

  const onChangeStates = useCallback(() => {
    if (!product) return;
    let newValidations: any;

    if (product.charge_type === EChargeType.SINGLE) {
      newValidations = { ...run(product, Validations) };
    } else if (product.charge_type === EChargeType.SUBSCRIPTION) {
      newValidations = { ...run(product, Validations) };
    } else {
      newValidations = { ...run(product, Validations) };
    }

    setValidationErrors(newValidations);

    if (onChangeRequiredFields) {
      onChangeRequiredFields('salesRequiredFields', newValidations ? Object.keys(newValidations).length : 0);
    }
  }, [product, onChangeRequiredFields]);

  const handleToggleAlertVisibility = useCallback((stateName: AlertsDefaultOptionsStateName) => {
    const setters: Record<AlertsDefaultOptionsStateName, React.Dispatch<React.SetStateAction<boolean>>> = {
      showAlertPix: setShowAlertPix,
      showAlertAutomaticPix: setShowAlertAutomaticPix,
      showAlertCard: setShowAlertCard,
      showAlertBoleto: setShowAlertBoleto,
      showAlertMultipleCards: setShowAlertMultipleCards,
      showAlertEduzzBalance: setShowAlertEduzzBalance,
      showAlertCombinedPayment: setShowAlertCombinedPayment
    };
    setters[stateName]?.(prev => !prev);
  }, []);

  const handleConfirmBeforeChange = useCallback(
    (fieldName: string) => {
      const fieldConfirmToggleState = FIELD_TO_ALERT_STATE[fieldName];

      if (fieldConfirmToggleState) {
        handleToggleAlertVisibility(fieldConfirmToggleState);
      }
    },
    [handleToggleAlertVisibility]
  );

  useEffect(() => {
    if (!loading) onChangeStates();
  }, [loading, onChangeStates]);

  useEffect(() => {
    if (loading) return;

    if (prevProductRef.current !== product) {
      prevProductRef.current = product;
      onChangeStates();
    }
  }, [loading, product, onChangeStates]);

  useEffect(() => {
    if (loading || !originalUserType || isLegalPerson || !product?.allows_automatic_pix) {
      return;
    }

    handleChange('allows_automatic_pix', false);
  }, [handleChange, isLegalPerson, loading, originalUserType, product?.allows_automatic_pix]);

  if (!product) return null;

  const isAlumy = product.cnt_partner_cod === EPartnerCode.ALUMY;
  const isInfo = product.type === constants.INFO;
  const isEvent = product.type === constants.TICKET;
  const isSinglePayment = product.charge_type === EChargeType.SINGLE;
  const isOpenValuePayment = product?.charge_type === EChargeType.OTHER;
  const isSubscriptionPayment = product.charge_type === EChargeType.SUBSCRIPTION;
  const isFree = product.charge_type === EChargeType.FREE;
  const isPhysical = product?.type === constants.PHYSICAL;
  const showInternationalChip = isInfo && isSinglePayment;
  const isUnlimitedInstallment = product.unlimited_installment;
  const userIsBetaEduzzBalance = eduzzAccountsService.verifyUserHasBetaModule('saldo_eduzz');
  const productIsBetaEduzzBalance = product.beta.includes('saldo_eduzz');
  const showEduzzBalance = (userIsBetaEduzzBalance || productIsBetaEduzzBalance) && (isEvent || isInfo);
  const productFriendlyNamePrefix = 'EDZ';
  const isCreditCardInstallments =
    product.credit_card_installments.producer || product.credit_card_installments.product;

  const paymentMethods = [
    {
      label: 'Permitir boleto',
      id: 'allows_boleto',
      checked: product.allows_boleto,
      helperText: 'Permitir pagamento por boleto no checkout?',
      show: true,
      error: 'allows_payment_types',
      disabled: isAlumy || product.trial,
      stateAlert: 'showAlertBoleto'
    },
    {
      label: 'Permitir cartão de crédito',
      id: 'allows_card',
      checked: product.allows_card,
      helperText: 'Permitir pagamento com cartão no checkout?',
      show: true,
      disabled: isAlumy,
      error: 'allows_payment_types',
      stateAlert: 'showAlertCard',
      ...(showInternationalChip && { chip: 'Disponível para venda internacional' })
    },
    {
      label: 'Permitir múltiplos cartões',
      id: 'allows_multiple_cards',
      checked: product.allows_multiple_cards,
      helperText: 'Permitir pagamento com dois ou mais cartões no checkout?',
      disabled: !product.allows_card,
      show: !isSubscriptionPayment,
      stateAlert: 'showAlertMultipleCards'
    },
    {
      label: 'Permitir Pix',
      id: 'allows_pix',
      checked: product.allows_pix,
      helperText: 'Permitir pagamento com pix no checkout?',
      show: isSinglePayment || isSubscriptionPayment || isOpenValuePayment,
      error: 'allows_payment_types',
      stateAlert: 'showAlertPix'
    },
    {
      label: 'Permitir Pix Automático',
      id: 'allows_automatic_pix',
      checked: isLegalPerson && product.allows_automatic_pix,
      helperText: isLegalPerson
        ? 'Permitir pagamentos de assinaturas via Pix Automático no checkout.'
        : 'O Pix Automático está disponível apenas para produtores com cadastro de Pessoa Jurídica (PJ).',
      show: isSubscriptionPayment,
      disabled: !isLegalPerson,
      error: 'allows_payment_types',
      stateAlert: 'showAlertAutomaticPix'
    },
    {
      label: 'Permitir Combinar Pagamento',
      id: 'allows_combine_payment',
      checked:
        product.allows_combine_payment &&
        product.allows_card &&
        product.allows_pix &&
        !isCreditCardInstallments &&
        !isUnlimitedInstallment,
      helperText: isCreditCardInstallments
        ? 'Essa forma de pagamento não está disponível para Recebimento no Fluxo'
        : 'Permitir pagamento combinado com cartão e pix?',
      show: isSinglePayment && isInfo,
      error: 'allows_payment_types',
      disabled: isCreditCardInstallments || isUnlimitedInstallment,
      stateAlert: 'showAlertCombinedPayment'
    },
    {
      label: 'Permitir Saldo Eduzz',
      id: 'allows_eduzz_balance',
      checked: product.allows_eduzz_balance,
      helperText: (
        <Typography.Paragraph>
          Permitir pagamentos com Saldo Eduzz no checkout?{' '}
          <a target='_blank' rel='noopener noreferrer' href='https://ajuda.eduzz.com/o-que-%C3%A9-o-saldo-eduzz'>
            Saiba mais
          </a>
        </Typography.Paragraph>
      ),
      show: showEduzzBalance,
      stateAlert: 'showAlertEduzzBalance'
    },
    {
      label: 'Parcelamento sem juros',
      id: 'interest_free_installment',
      checked: product.interest_free_installment,
      helperText: (
        <Typography.Paragraph>
          As parcelas no checkout irão aparecer sem os juros. Nesse caso o produtor assume os juros no lugar do
          comprador.{' '}
          <a
            target='_blank'
            rel='noopener noreferrer'
            href='https://ajuda.eduzz.com/central-de-ajuda/hc/pt-br/articles/1260807551369-O-que-%C3%A9-Parcelamento-sem-juros'
          >
            Saiba Mais
          </a>
        </Typography.Paragraph>
      ),
      show: !isFree && !isOpenValuePayment
    },
    {
      label: 'Parcelamento sem limites',
      id: 'unlimited_installment',
      checked: product.unlimited_installment,
      helperText: (
        <Typography.Paragraph>
          Caso o seu cliente não tenha limite no cartão você pode oferecer o pagamento em parcelas recorrentes.{' '}
          <a target='_blank' rel='noopener noreferrer' href='https://ajuda.eduzz.com/hc/pt-br/articles/1260807548029'>
            Saiba Mais
          </a>
        </Typography.Paragraph>
      ),
      show: isSinglePayment && !isPhysical
    },
    {
      label: 'Entregar conteúdo apenas na primeira cobrança paga',
      id: 'deliver_first_charge',
      checked: product.deliver_first_charge,
      helperText:
        'O cliente receberá a entrega do conteúdo apenas uma vez, ao realizar o pagamento da adesão ou da primeira cobrança.',
      show: isSinglePayment ? product.unlimited_installment : isSubscriptionPayment,
      disabled: isAlumy
    }
  ] as PaymentMethod[];

  return (
    <>
      <Row gutter={[16, 16]} align='middle'>
        <Col span={24}>
          <Alert
            type='info'
            showIcon
            icon={<Icon.Info  />}
            message='Ativamos nossas ferramentas para te ajudar a converter mais. Você pode desativá-las quando quiser, mas antes, confira os dados e impactos que levantamos para apoiar sua decisão.'
          />
        </Col>

        <Col span={24}>
          <FormContent title='Meios de pagamento e checkout' loading={loading}>
            <Row gutter={[16, 16]} align='middle'>
              {paymentMethods
                .filter(pm => pm.show)
                .map((p, index) => {
                  const hasError = isValid(p.error);
                  const isFirst = index === 0;

                  const label = p.chip ? (
                    <>
                      {p.label} <Tag color='processing'>{p.chip}</Tag>
                    </>
                  ) : (
                    p.label
                  );
                  return (
                    <React.Fragment key={p.id}>
                      {!isFirst && (
                        <Col span={24}>
                          <Divider className='my-0!' />
                        </Col>
                      )}
                      <Col span={24}>
                        <div className='flex items-center gap-2'>
                          <Switch
                            checked={p.checked}
                            disabled={p.disabled}
                            onChange={checked => {
                              const willUncheck = !checked;

                              if (willUncheck && paymentShouldConfirmToDisable.includes(p.id)) {
                                return handleConfirmBeforeChange(p.id);
                              }

                              if (p.id === 'allows_combine_payment' && checked) {
                                handleChange('allows_card', true);
                                handleChange('allows_pix', true);
                              }

                              return handleChange(p.id, checked);
                            }}
                          />
                          <Typography.Text className='text-base'>{label}</Typography.Text>
                        </div>
                        {hasError && (
                          <Typography.Paragraph className='mt-1 text-base text-(--uizz-antd-color-error)'>
                            {errorFor(p.error)}
                          </Typography.Paragraph>
                        )}
                        {!hasError && (
                          <Typography.Paragraph className='text-content-subtitle mt-1! mb-0! text-base'>
                            {p.helperText}
                          </Typography.Paragraph>
                        )}
                      </Col>
                      {((p.id === 'allows_multiple_cards' && !isSubscriptionPayment) ||
                        (p.id === 'allows_card' && isSubscriptionPayment)) && (
                        <>
                          <Col span={24}>
                            <Divider className='my-0!' />
                          </Col>
                          <Col span={24}>
                            <CharacterCounter
                              helperText={
                                isValid('friendly_name')
                                  ? errorFor('friendly_name')
                                  : 'A identificação na fatura do cartão é importante para prevenir chargebacks. Utilize um nome intuitivo que seu cliente irá reconhecer ao ver a fatura'
                              }
                              label='Nome exibido na fatura de cartão'
                              id='friendly_name'
                              error={isValid('friendly_name')}
                              value={
                                product.friendly_name &&
                                product.friendly_name.startsWith(`${productFriendlyNamePrefix} `)
                                  ? product.friendly_name.slice(4)
                                  : product.friendly_name
                              }
                              onChange={(e: any) => handleChange('friendly_name', e.target.value)}
                              prefix={productFriendlyNamePrefix}
                              limits={10}
                              minimum={0}
                              rows={1}
                            />
                          </Col>
                        </>
                      )}
                    </React.Fragment>
                  );
                })}
            </Row>
            {isSinglePayment && isInfo && (
              <Row gutter={[16, 16]} className='mt-3'>
                <Col span={24}>
                  <Alert
                    type='warning'
                    showIcon
                    icon={<Icon.TriangleAlert  />}
                    message='Para vendas feitas com moedas estrangeiras a opção de cartão de crédito será ativada automaticamente e as demais opções serão desativadas'
                  />
                </Col>
              </Row>
            )}
          </FormContent>
        </Col>
      </Row>

      <AlertsDefaultOptions
        showAlert={{
          pix: showAlertPix,
          automaticPix: showAlertAutomaticPix,
          card: showAlertCard,
          boleto: showAlertBoleto,
          multipleCards: showAlertMultipleCards,
          eduzzBalance: showAlertEduzzBalance,
          combinedPayment: showAlertCombinedPayment
        }}
        toggleAlert={(alertName: AlertsDefaultOptionsStateName) => handleToggleAlertVisibility(alertName)}
        onToggleConfirmed={(fieldId: string, checked: boolean) => handleChange(fieldId, checked)}
      />
    </>
  );
}

export default PaymentMethods;
